import { Helmet } from 'react-helmet';
import { useLocation } from '@/lib/router-compat';
import { 
  BASE_URL, 
  getOrganizationSchema, 
  getWebsiteSchema, 
  getBreadcrumbSchema, 
  getFAQSchema,
  getArticleSchema,
  getServiceSchema,
  getHreflangLinks,
  getCanonicalUrl,
  getBasePathFromUrl
} from '@/lib/seo';
import { getLanguageFromUrl } from '@/hooks/useLanguageRouting';

export interface SEOHeadProps {
  titleEn: string;
  titleDe: string;
  descriptionEn: string;
  descriptionDe: string;
  keywords?: string | undefined;
  path?: string | undefined;
  image?: string | undefined;
  type?: 'website' | 'article' | undefined;
  publishedTime?: string | undefined;
  modifiedTime?: string | undefined;
  author?: string | undefined;
  breadcrumbs?: { name: string; nameDE: string; url: string }[] | undefined;
  faqs?: { question: string; answer: string }[] | undefined;
  includeOrganization?: boolean | undefined;
  includeWebsite?: boolean | undefined;
  includeService?: boolean | undefined;
  article?: {
    title: string;
    description: string;
    url: string;
    image: string;
    datePublished: string;
    dateModified?: string | undefined;
    author?: string | undefined;
    authorUrl?: string | undefined;
    authorJobTitle?: string | undefined;
    reviewer?: string | undefined;
  } | undefined;
  noindex?: boolean | undefined;
}

const SEOHead = ({
  titleEn,
  titleDe,
  descriptionEn,
  descriptionDe,
  keywords,
  path,
  image = `${BASE_URL}/favicon.png`,
  type = 'website',
  publishedTime,
  modifiedTime,
  author = 'Nearshore.Africa',
  breadcrumbs = [],
  faqs,
  includeOrganization = true,
  includeWebsite = false,
  includeService = false,
  article,
  noindex = false
}: SEOHeadProps) => {
  const location = useLocation();
  const currentPath = path || location.pathname;
  
  // Get language from URL (German default, English with /en prefix)
  const currentLanguage = getLanguageFromUrl(currentPath);
  const basePath = getBasePathFromUrl(currentPath);
  
  const isGerman = currentLanguage === 'de';
  const title = isGerman ? titleDe : titleEn;
  const description = isGerman ? descriptionDe : descriptionEn;
  
  // Generate canonical for current language and hreflang for all languages
  const canonicalUrl = getCanonicalUrl(basePath, currentLanguage);
  const hreflangLinks = getHreflangLinks(basePath);
  
  // Generate breadcrumb schema with language-appropriate names and URLs
  const breadcrumbItems = breadcrumbs.map(bc => {
    const bcBasePath = getBasePathFromUrl(bc.url);
    return {
      name: isGerman ? bc.nameDE : bc.name,
      url: bc.url.startsWith('http') 
        ? bc.url 
        : `${BASE_URL}${isGerman ? bcBasePath : `/en${bcBasePath === '/' ? '' : bcBasePath}`}`
    };
  });
  
  // Collect all JSON-LD schemas
  const schemas: object[] = [];
  
  if (includeOrganization) {
    schemas.push(getOrganizationSchema());
  }
  
  if (includeWebsite) {
    schemas.push(getWebsiteSchema());
  }
  
  if (includeService) {
    schemas.push(getServiceSchema());
  }
  
  if (breadcrumbItems.length > 0) {
    schemas.push(getBreadcrumbSchema(breadcrumbItems));
  }
  
  if (faqs && faqs.length > 0) {
    schemas.push(getFAQSchema(faqs));
  }
  
  if (article) {
    schemas.push(getArticleSchema(article));
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="language" content={isGerman ? 'German' : 'English'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags */}
      {hreflangLinks.map((link, index) => (
        <link 
          key={index}
          rel={link.rel} 
          hrefLang={link.hrefLang} 
          href={link.href} 
        />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Nearshore.Africa" />
      <meta property="og:locale" content={isGerman ? 'de_DE' : 'en_US'} />
      <meta property="og:locale:alternate" content={isGerman ? 'en_US' : 'de_DE'} />
      
      {/* Article specific Open Graph */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="DE" />
      <meta name="geo.placename" content="Stuttgart" />
      <meta name="revisit-after" content="7 days" />
      
      {/* JSON-LD Structured Data */}
      {schemas.map((schema, index) => (
        <script 
          key={index}
          type="application/ld+json"
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
